import React, { useState, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Loader2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

interface UserAvatarUploadProps {
  avatarUrl?: string | null;
  onUploadComplete: (url: string) => void;
  fullName?: string | null;
}

export function UserAvatarUpload({
  avatarUrl,
  onUploadComplete,
  fullName,
}: UserAvatarUploadProps) {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      if (!event.target.files || event.target.files.length === 0) {
        return;
      }

      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `${user?.id}-${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      setUploading(true);

      // 1. Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      // 2. Get public URL
      const {
        data: { publicUrl },
      } = supabase.storage.from('avatars').getPublicUrl(filePath);

      // 3. Update user profile
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user?.id);

      if (updateError) {
        throw updateError;
      }

      onUploadComplete(publicUrl);
      toast.success('Avatar updated successfully');

      // Force page reload to refresh avatar in all components
      window.location.reload();
    } catch (error: unknown) {
      console.error('Error uploading avatar:', error);
      const msg = error instanceof Error ? error.message : 'Unknown error';
      toast.error('Error uploading avatar: ' + msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      setUploading(true);

      // 1. Delete from storage if avatar exists
      if (avatarUrl) {
        const fileName = avatarUrl.split('/').pop();
        if (fileName) {
          const { error: deleteError } = await supabase.storage
            .from('avatars')
            .remove([fileName]);

          if (deleteError) {
            console.warn('Error deleting avatar file:', deleteError);
            // Continue anyway to clear the avatar_url from profile
          }
        }
      }

      // 2. Update user profile to remove avatar_url
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ avatar_url: null })
        .eq('id', user?.id);

      if (updateError) {
        throw updateError;
      }

      onUploadComplete('');
      toast.success('Avatar removed successfully');

      // Force page reload to refresh avatar in all components
      window.location.reload();
    } catch (error: unknown) {
      console.error('Error removing avatar:', error);
      const msg = error instanceof Error ? error.message : 'Unknown error';
      toast.error('Error removing avatar: ' + msg);
    } finally {
      setUploading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const getInitials = (name: string) => {
    return name
      ?.split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative group">
        <Avatar className="h-24 w-24 cursor-pointer" onClick={triggerFileInput}>
          <AvatarImage src={avatarUrl || ''} alt={fullName || 'User avatar'} />
          <AvatarFallback className="text-lg">
            {fullName ? getInitials(fullName) : 'U'}
          </AvatarFallback>

          {/* Overlay on hover */}
          <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
            <Upload className="h-6 w-6 text-white" />
          </div>
        </Avatar>

        {uploading && (
          <div className="absolute inset-0 bg-black/60 rounded-full flex items-center justify-center">
            <Loader2 className="h-8 w-8 text-white animate-spin" />
          </div>
        )}
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/*"
        className="hidden"
        disabled={uploading}
      />

      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={triggerFileInput}
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : 'Change Avatar'}
        </Button>
        {avatarUrl && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleRemoveAvatar}
            disabled={uploading}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <X className="h-4 w-4 mr-1" />
            Remove
          </Button>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Click to upload. JPG, PNG or GIF. Max 2MB.
      </p>
    </div>
  );
}
